/* eslint-disable react/destructuring-assignment */
/* eslint-disable no-use-before-define */
/* eslint-disable react/jsx-no-bind */
import Icon, { ArrowDownOutlined, ArrowUpOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { useLazyQuery, useQuery } from "@apollo/client";
import { Badge, Button, Checkbox, Input, Popover, Switch, Tabs, Tooltip, message } from "antd";
import { isEmpty } from "lodash";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import EngagedAI from "../../../../static/EngagedAi_trans_bg.svg";
import TranslateIcon from "../../../../static/icons/TranslateIcon.svg";
import { UserContext } from "../../../amplify/authenticator-provider";
import { GET_AI_RESPONSE, GET_AI_SUGGESTION_QUICK_TIP } from "../../../graphql/query";
import AiSettingsIcon from "../../../icons/AiSettingsIcon";
import { SVGIcon } from "../../../library/basicComponents";
import { colorConfig } from "../../../library/colorConfig";
import { AccessPermissionAction } from "../../../library/constants";
import { checkAccessPermission } from "../../../library/helpers";
import { applicationUseCaseMapping, getApplicationUseCaseId } from "../../../library/utils";
import ChatMagic from "./chatMagic";
import "./editor.css";
import { addFilesToAttachments, InputTextAreaWrap } from "./engagementActivity";
import { setTextMode } from "../../../redux/slices/leadSlice";
import { setTextAiResponseHistoryId } from "../../../redux/slices/aiSlice";
import { getMobileView } from "../../../redux/slices/isMobile";
import useBrowserExt from "../../../hooks/useBrowserExt";
import { EmailTextBox } from "./EmailTextBox";
import { ADDING_TYPE } from "../../adminDashboard/settings/CustomizeAiSuggestions";
import CustomSkeleton from "../../commonComponents/CustomSkeleton";

// Re-export the wrapper as the default export
export { InputAreaWrapper } from "./InputAreaWrapper";

// Styled Tabs component that makes tab content panels take full height
const StyledTabs = styled(Tabs)`
  height: 100%;
  display: flex;
  flex-direction: column;

  .ant-tabs-content-holder {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
  }

  .ant-tabs-content {
    height: 100%;
  }

  .ant-tabs-tabpane {
    height: 100%;
  }
`;

const CustomizePrompt = ({
  selectedLead,
  user,
  text,
  popupRef,
  setmessageText,
  setloaderFn,
  selectedOppTypeId,
}) => {
  const [customizePromptData, setcustomizePromptData] = useState({
    visible: false,
    quickTips: [],
    tones: [],
    additionalNote: "",
  });
  const Divider = () => <div className="w-full h-[2px] bg-grey-200 " />;
  const SectionHeader = ({ children }) => (
    <span className="text-sm font-[400] text-grey-700">{children}</span>
  );
  const Toggle = ({ children, onChange, value }) => (
    <div className="flex gap-1 flex-row items-center">
      <Switch onChange={onChange} checked={value} size="small" /> {children}
    </div>
  );
  const TabToggle = ({ children, isActive, onChange }) => {
    return (
      <div
        onClick={onChange}
        className={`px-4 py-2 cursor-pointer rounded border-[1px] border-solid  ${
          isActive ? "border-brand-500 bg-brand-50" : "border-grey-300"
        }`}
      >
        {children}
      </div>
    );
  };

  const [dynamicTips, setdynamicTips] = useState([]);
  const [dynamicTones, setdynamicTones] = useState([]);
  const [isEnableAdditionalComments, setisEnableAdditionalComments] = useState(false);
  const { applicationUseCases } = useSelector(state => state.aiData);
  const dispatch = useDispatch();
  const [getAiResponse, { loading: getAiResponseLoader }] = useLazyQuery(GET_AI_RESPONSE, {
    nextFetchPolicy: "network-only",
  });

  const { loading: quickTipLoader } = useQuery(GET_AI_SUGGESTION_QUICK_TIP, {
    // Default ``cache-first`` policy: Apollo returns cached results when
    // these exact variables have been seen before, so re-opening the
    // popover for the same (location, tab, use-case) combo skips the
    // network entirely. Switching the active opp-type tab invalidates
    // the cache key naturally because ``rootOppTypeId`` is in the
    // variable set.
    variables: {
      companyId: user.company.id,
      // When a tab is active we let BE derive the tab's department(s)
      // from ``rootOppTypeId`` — drop ``departmentIds`` so the
      // resolver's department-derivation branch fires. Without an
      // active tab we keep the legacy ``user.departmentIds`` path.
      locationIds:
        !selectedOppTypeId && Array.isArray(user?.departmentIds) && user.departmentIds.length > 0
          ? null
          : sessionStorage.getItem("currentLocationId")?.split(","),
      applicationUseCaseId: getApplicationUseCaseId(
        applicationUseCases,
        applicationUseCaseMapping.ENGAGEMENT_MESSAGE_SUGGESTION
      )?.id,
      departmentIds: selectedOppTypeId ? null : user?.departmentIds || null,
      rootOppTypeId: selectedOppTypeId || null,
    },
    onCompleted: res => {
      // filter out active tips only
      const activeTips = (res?.getAiSuggestionQuickTip || [])?.filter(
        tip => tip?.isActive && tip?.suggestionType === ADDING_TYPE.TIPS
      );

      // filter out active tones only
      const activeTones = (res?.getAiSuggestionQuickTip || [])?.filter(
        tip => tip?.isActive && tip?.suggestionType === ADDING_TYPE.TONES
      );

      // filter out active additional comments only
      const additionalComments = (res?.getAiSuggestionQuickTip || [])?.filter(
        tip => tip?.isActive && tip?.suggestionType === ADDING_TYPE.ADDITIONAL_NOTES
      );

      setdynamicTips(activeTips);
      setdynamicTones(activeTones);
      setisEnableAdditionalComments(additionalComments.length > 0);
    },
  });

  const close = () => popupRef?.current?.click();

  const getAiResponseHandler = async () => {
    text.current.placeholder = "Generating response...";
    text.current.value = "";
    text.current.style.height = "0px";
    text.current.focus();
    setmessageText("");
    const promptData = { ...customizePromptData };
    delete promptData.visible;
    const payload = {
      aiSuggestionUserInput: { ...promptData },
      leadId: selectedLead.id,
      companyId: user.company.id,
      applicationUseCaseId: getApplicationUseCaseId(
        applicationUseCases,
        applicationUseCaseMapping.ENGAGEMENT_MESSAGE_SUGGESTION
      )?.id,
    };
    setloaderFn(true);
    const res = await getAiResponse({
      variables: payload,
    });
    dispatch(setTextAiResponseHistoryId(res?.data?.getAiResponse?.aiResponseHistoryId));
    const writeText = res?.data?.getAiResponse?.content;
    let index = 0;
    if (!isEmpty(writeText)) {
      typeCharacter();
    } else {
      setloaderFn(false);
    }
    function typeCharacter() {
      if (index < writeText.length) {
        if (!isEmpty(text?.current)) {
          text.current.style.height = `${text.current.scrollHeight}px`;
          if (isEmpty(text?.current?.value)) {
            text.current.value = writeText.charAt(index);
          } else {
            text.current.value += writeText.charAt(index);
          }
          index++;
          if (index === writeText.length) {
            setmessageText(writeText);
            setloaderFn(false);
          }
          setTimeout(typeCharacter, 20); // Adjust the delay (in milliseconds) between characters here
        }
      }
    }
    // text.current.value
    close();
  };
  return (
    <div className="flex flex-col" onClick={e => e.stopPropagation()}>
      <div className="flex flex-row p-3 bg-[#F1F5F9] justify-between">
        <span className="text-sm font-[600] text-grey-700 ">Customize AI Suggestion</span>
        <CloseCircleOutlined onClick={close} className="cursor-pointer" />
      </div>
      <div className="flex flex-col py-2 px-4 items-start ">
        <SectionHeader>Quick Tips</SectionHeader>
        <div className="flex flex-col items-start gap-1 w-full">
          {quickTipLoader ? (
            <CustomSkeleton skeletonCount={1} />
          ) : !isEmpty(dynamicTips) ? (
            dynamicTips.map((item, index) => (
              <Toggle
                key={index}
                onChange={checked => {
                  if (checked) {
                    setcustomizePromptData(prev => {
                      const temp = { ...prev };
                      temp.quickTips.push(item.tip);
                      return temp;
                    });
                  } else {
                    setcustomizePromptData(prev => {
                      const temp = structuredClone(prev);
                      const index = temp.quickTips.indexOf(item.tip);
                      temp.quickTips.splice(index, 1);
                      return temp;
                    });
                  }
                }}
                value={customizePromptData.quickTips.includes(item.tip)}
              >
                {item.tip}
              </Toggle>
            ))
          ) : (
            <span className="text-sm text-grey-600">No Quick Tips found</span>
          )}
        </div>
      </div>
      <Divider />
      <div className="flex flex-col py-2 px-4 items-start">
        <SectionHeader>Tone</SectionHeader>
        <div className="flex flex-row flex-wrap gap-4">
          {quickTipLoader ? (
            <CustomSkeleton skeletonCount={1} />
          ) : !isEmpty(dynamicTones) ? (
            dynamicTones.map((item, index) => (
              <TabToggle
                key={index}
                onChange={() => {
                  setcustomizePromptData(prev => {
                    const temp = structuredClone(prev);
                    temp.tones = [item.tip];
                    return temp;
                  });
                }}
                isActive={customizePromptData?.tones?.includes(item.tip)}
              >
                {item.tip}
              </TabToggle>
            ))
          ) : (
            <span className="text-sm text-grey-600">No Tones found</span>
          )}
        </div>
      </div>
      <Divider />
      <div className="flex flex-col py-2 gap-1 px-4 items-start">
        {isEnableAdditionalComments && (
          <>
            <SectionHeader>Additional Notes</SectionHeader>
            <Input.TextArea
              value={customizePromptData.additionalNote}
              onChange={e =>
                setcustomizePromptData({ ...customizePromptData, additionalNote: e.target.value })
              }
              autoSize={{ minRows: 3 }}
              placeholder="Type some notes..."
            />
          </>
        )}
        <Checkbox
          checked={customizePromptData.save}
          onChange={e => setcustomizePromptData({ ...customizePromptData, save: e.target.checked })}
        >
          Save these options
        </Checkbox>
        {customizePromptData.save && (
          <Input
            value={customizePromptData.filterName}
            onChange={e =>
              setcustomizePromptData({ ...customizePromptData, filterName: e.target.value })
            }
            placeholder="Enter preset customize name"
          />
        )}
        <Button
          loading={getAiResponseLoader}
          onClick={getAiResponseHandler}
          type="primary"
          className="w-full rounded-sm"
        >
          Regenerate
        </Button>
      </div>
    </div>
  );
};

function SuggestAMessageButton({
  createSuggestedResponse,
  misc,
  selectedLead,
  disabled,
  text,
  setinputText,
  setloaderFn,
  selectedOppTypeId,
}) {
  const popupRef = useRef(null);
  const { user } = useContext(UserContext);

  return (
    <Button
      key={selectedLead?.id}
      loading={misc.loading}
      className="text-lg flex bg-white !border !border-solid !border-[#E2E8F0] hover:border-separate flex-row items-center justify-between p-1 gap-1"
      disabled={disabled}
      // type="text"
    >
      <Tooltip title="Suggest message">
        <Icon component={EngagedAI} alt="EngagedAi" onClick={() => createSuggestedResponse()} />
      </Tooltip>
      <div className="h-[20px] w-[1px] bg-[#E2E8F0]" />
      <Tooltip title="Customize AI Suggestion">
        <Popover
          className=""
          overlayClassName="ai-prompt-popup"
          trigger="click"
          destroyTooltipOnHide
          content={
            <CustomizePrompt
              user={user}
              text={text}
              selectedLead={selectedLead}
              popupRef={popupRef}
              setmessageText={setinputText}
              setloaderFn={setloaderFn}
              selectedOppTypeId={selectedOppTypeId}
            />
          }
        >
          <span
            onClick={e => {
              e.stopPropagation();
            }}
            className="flex-row-centered items-center"
            ref={popupRef}
          >
            <AiSettingsIcon className="scale-75 mt-1" />
          </span>
        </Popover>
      </Tooltip>
    </Button>
  );
}

// InputArea is now wrapped by InputAreaWrapper which uses key={selectedLeadId}
// This means the component will ONLY remount when lead changes
// Scroll/message updates will NOT affect this component at all!
//
// PLUS: We wrap with React.memo to prevent re-renders even when props change
// The memo comparison only checks if the TEXT REF changes (which it never does)
export const InputArea = React.memo(
  function InputArea(props) {
    const { textMode, selectedLead } = useSelector(state => state.lead);
    const dispatch = useDispatch();
    const { isBrowserExt } = useBrowserExt();
    const isMobileView = useSelector(state => getMobileView(state));
    const [loaderFn, setloaderFn] = useState(false);
    const canDisabledConversation = checkAccessPermission(
      AccessPermissionAction.LEAD_MANAGEMENT_DISABLED_CONVERSATION
    );

    const canSendEmail = checkAccessPermission(AccessPermissionAction.EMAIL_MANAGEMENT_SEND);

    // Mark textarea with AI generating state so BottomActions can check it
    useEffect(() => {
      if (props?.text?.current) {
        props.text.current.dataset.aiGenerating = loaderFn ? "true" : "false";
      }
    }, [loaderFn]);

    // Since this component remounts on lead change (via key prop),
    // we can safely clear the input on mount
    useEffect(() => {
      if (props?.text?.current) {
        props.text.current.value = "";
        props.text.current.style.height = "0px";
      }
    }, []); // Empty deps - only on mount (which happens on lead change)

    useEffect(() => {
      if (!props?.messageText) {
        props?.setOriginalText?.("");
      }
    }, [props?.messageText, props?.setOriginalText]);

    useEffect(() => {
      // Set default active tab based on phone/email availability
      if (selectedLead) {
        const hasPhone = !isEmpty(selectedLead.phoneNumbers);
        const hasEmail = !isEmpty(selectedLead.emails);

        if (!hasPhone && hasEmail) {
          dispatch(setTextMode("Email"));
        } else {
          dispatch(setTextMode("Text"));
        }
      }
    }, []); // Empty deps - only set on mount

    const componentProps = {
      ...props,
      selectedLead, // Get from Redux, not props!
      setInputMode: mode => dispatch(setTextMode(mode)),
      loaderFn,
      setloaderFn,
      canDisabledConversation,
      disableToEmail: !canSendEmail,
    };

    const hasPhone = !isEmpty(selectedLead?.phoneNumbers);
    const hasEmail = !isEmpty(selectedLead?.emails);

    // Don't recreate tabItems on every render - memoize the children components
    // This prevents Ant Design Tabs from unmounting/remounting the textarea
    const smsTextBox = React.useMemo(() => <SMSTextBox {...componentProps} />, [componentProps]);
    const emailTextBox = React.useMemo(
      () => <EmailTextBox {...componentProps} useFlexLayout />,
      [componentProps]
    );

    const tabItems = [
      {
        label: "Text",
        key: "Text",
        disabled: isEmpty(selectedLead) || !hasPhone,
        children: smsTextBox,
      },
      {
        label: "Email",
        key: "Email",
        disabled: isEmpty(selectedLead) || !hasEmail || !canSendEmail,
        children: emailTextBox,
      },
    ];

    return (
      <div className="flex flex-row justify-center h-full relative">
        <StyledTabs
          activeKey={textMode}
          onChange={key => dispatch(setTextMode(key))}
          className="w-full"
          items={tabItems}
          size="small"
          tabBarExtraContent={
            !isBrowserExt && isMobileView ? (
              <Tooltip title={props.hideBottom ? "Hide" : "Show"}>
                <Badge
                  count={!props.hideBottom ? props.messageFilters?.campaignIds?.length : 0}
                  style={{ backgroundColor: "red" }}
                >
                  <span
                    className="w-6 h-6 rounded-full bg-twLinkColor text-white flex items-center justify-center cursor-pointer mr-2"
                    onClick={() => props.setHideBottom(!props.hideBottom)}
                  >
                    {props.hideBottom ? (
                      <ArrowDownOutlined className="text-xs" />
                    ) : (
                      <ArrowUpOutlined className="text-xs" />
                    )}
                  </span>
                </Badge>
              </Tooltip>
            ) : null
          }
        />
        {textMode === "Text" && (
          <div className="flex absolute right-3 bottom-3 items-center gap-1 justify-center z-20">
            {componentProps.isTranslatePreviewShow && (
              <Tooltip
                title={componentProps.originalText ? "Back to original text" : "Translate text"}
              >
                <span
                  onClick={() => {
                    if (!(componentProps.text.current?.value.length > 0)) {
                      message.info("Please enter message to translate.");
                      return;
                    }
                    if (componentProps.text.current?.value && !componentProps.originalText) {
                      componentProps.setOriginalText(componentProps.text.current.value);
                      componentProps.translateMessage({
                        variables: {
                          content: componentProps.text.current?.value,
                          targetLanguage: selectedLead?.preferredLanguageCode,
                        },
                        onCompleted: res => {
                          if (res?.translateMessage?.statusCode === 200) {
                            const translatedText = res?.translateMessage?.translatedContent;
                            componentProps.text.current.value = translatedText;
                            componentProps.text.current.style.height = `${componentProps.text.current.scrollHeight}px`;
                            componentProps.setmessageText(translatedText);
                            message.info(res?.translateMessage?.message);
                          } else {
                            message.info(res?.translateMessage?.message);
                          }
                        },
                      });
                      return;
                    }
                    componentProps.text.current.value = componentProps.originalText;
                    componentProps.text.current.style.height = `${componentProps.text.current.scrollHeight}px`;
                    componentProps.setmessageText(componentProps.originalText);
                    componentProps.setOriginalText("");
                  }}
                >
                  <SVGIcon component={TranslateIcon} alt="Close" />
                </span>
              </Tooltip>
            )}
            <ChatMagic
              text={componentProps.text}
              richTextValue={componentProps.richTextValue}
              setRichTextValue={componentProps.setrichTextValue}
              selectedLead={selectedLead}
              user={componentProps.user}
              placeholder={componentProps.placeholder}
              setmessageText={componentProps.setmessageText}
              displaySize="small"
              loaderFn={loaderFn}
              setloaderFn={setloaderFn}
              code="ENGAGEMENT_MESSAGE_SUGGESTION"
              messageText={componentProps.messageText}
              Component={({ createSuggestedResponse, misc }) => (
                <SuggestAMessageButton
                  createSuggestedResponse={createSuggestedResponse}
                  misc={misc}
                  disabled={
                    componentProps.textDisabled() ||
                    !selectedLead?.id ||
                    componentProps.messageLoader ||
                    componentProps.loading ||
                    componentProps.spinner ||
                    componentProps.leadsLoading
                  }
                  text={componentProps.text}
                  loaderFn={loaderFn}
                  setloaderFn={setloaderFn}
                  selectedLead={selectedLead}
                  setinputText={componentProps.setmessageText}
                  selectedOppTypeId={componentProps.selectedOppTypeId}
                />
              )}
            />
          </div>
        )}
      </div>
    );
  },
  (prevProps, nextProps) => {
    // CRITICAL: This comparison determines when to re-render
    // Return true = block re-render (props are "equal")
    // Return false = allow re-render (props changed)

    // IMPORTANT: We get selectedLead from Redux inside the component,
    // so we should IGNORE props.selectedLead changes completely!
    // The wrapper's key handles lead changes, so we only care about
    // the ID for safety check, not the object reference.

    // Safety check: If lead ID changed, allow re-render
    // (though wrapper's key should handle remounting already)
    const leadIdChanged = prevProps.selectedLead?.id !== nextProps.selectedLead?.id;
    if (leadIdChanged) {
      return false; // Allow re-render
    }

    // Lead ID is same - now check if any CRITICAL props changed
    // We explicitly DO NOT compare selectedLead object itself!
    // When messages load on scroll, selectedLead object changes but ID stays same
    const propsAreEqual =
      prevProps.text === nextProps.text &&
      prevProps.messageLoader === nextProps.messageLoader &&
      prevProps.loading === nextProps.loading &&
      prevProps.spinner === nextProps.spinner &&
      prevProps.leadsLoading === nextProps.leadsLoading &&
      prevProps.textDisabled === nextProps.textDisabled &&
      prevProps.messageText === nextProps.messageText &&
      prevProps.placeholder === nextProps.placeholder &&
      prevProps.buttonDisabled === nextProps.buttonDisabled &&
      prevProps.isEmojiOpen === nextProps.isEmojiOpen &&
      prevProps.setmessageText === nextProps.setmessageText;

    // Return true to block re-render if all critical props are equal
    return propsAreEqual;
  }
);
const SMSTextBox = React.memo(
  function SMSTextBox({
    buttonStyle,
    text,
    selectedLead,
    handleKeyDown,
    handleKeyUp,
    canDisabledConversation,
    textDisabled,
    messageLoader,
    loading,
    spinner,
    leadsLoading,
    loaderFn,
    lang,
    setisReview,
    placeholder,
    setmessageText,
    setAttachments,
  }) {
    const [isDragging, setIsDragging] = useState(false);
    const dragCounterRef = useRef(0);
    const [mediaProcessing, setMediaProcessing] = useState(false);

    const isUploadDisabled =
      canDisabledConversation ||
      textDisabled() ||
      !selectedLead?.id ||
      messageLoader ||
      loading ||
      spinner ||
      leadsLoading ||
      loaderFn;

    const eventHasFiles = e => {
      const types = e.dataTransfer?.types;
      if (!types) return false;
      // DOMStringList lacks Array methods in some browsers — index manually.
      for (let i = 0; i < types.length; i++) {
        if (types[i] === "Files") return true;
      }
      return false;
    };

    const handleDragEnter = e => {
      if (isUploadDisabled || !setAttachments) return;
      if (!eventHasFiles(e)) return;
      e.preventDefault();
      e.stopPropagation();
      dragCounterRef.current += 1;
      setIsDragging(true);
    };

    const handleDragOver = e => {
      if (isUploadDisabled || !setAttachments) return;
      if (!eventHasFiles(e)) return;
      e.preventDefault();
      e.stopPropagation();
      // Hint to the OS that this is a copy/upload target.
      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = "copy";
      }
    };

    const handleDragLeave = e => {
      if (isUploadDisabled || !setAttachments) return;
      if (!eventHasFiles(e)) return;
      e.preventDefault();
      e.stopPropagation();
      dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
      if (dragCounterRef.current === 0) {
        setIsDragging(false);
      }
    };

    const handleDrop = e => {
      if (isUploadDisabled || !setAttachments) return;
      if (!eventHasFiles(e)) return;
      e.preventDefault();
      e.stopPropagation();
      dragCounterRef.current = 0;
      setIsDragging(false);
      const files = e.dataTransfer?.files;
      if (files && files.length > 0) {
        addFilesToAttachments(files, setAttachments, setMediaProcessing);
      }
    };

    const handlePaste = e => {
      if (isUploadDisabled || !setAttachments) return;
      const items = e.clipboardData?.items;
      if (!items || items.length === 0) return;
      const files = [];
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === "file") {
          const file = item.getAsFile();
          if (file) files.push(file);
        }
      }
      if (files.length === 0) return;
      // Suppress the textarea's default paste so a pasted image's filename /
      // binary string doesn't end up as message text.
      e.preventDefault();
      addFilesToAttachments(files, setAttachments, setMediaProcessing);
    };

    return (
      <div
        className="relative h-full"
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="flex flex-grow h-full -mx-2">
          <InputTextAreaWrap
            key={`sms-textarea-${selectedLead?.id || "no-lead"}`}
            placeholderStyle={{
              color: buttonStyle()?.background === "#F59E0B" ? colorConfig.primaryDark : "",
            }}
            styling={buttonStyle()}
            className="relative resize-none focus-visible:outline-none border-none pr-[80px] pl-4 flex items-center rounded-[4px] pt-1 pb-5"
            id="text"
            ref={text}
            rows={1}
            onKeyDown={handleKeyDown}
            onKeyUp={handleKeyUp}
            onPaste={handlePaste}
            disabled={
              canDisabledConversation ||
              textDisabled() ||
              !selectedLead?.id ||
              messageLoader ||
              loading ||
              spinner ||
              leadsLoading ||
              loaderFn
            }
            onChange={e => {
              const newValue = e.target.value;
              if (newValue === "") {
                text.current.placeholder = lang.typeYourMessageHere;
                text.current.style.height = "0px";
                setisReview(false);
              }
              e.target.style.height = `${e.target.scrollHeight}px`;
              setmessageText(newValue);
            }}
            placeholder={placeholder()}
            name="text"
            maxLength={1000}
          />
        </div>
        {isDragging && !isUploadDisabled && (
          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-[4px] border-2 border-dashed border-brand-500 bg-brand-50/80 pointer-events-none">
            <p className="text-sm font-semibold text-brand-700">
              Drop images, videos, or documents to attach
            </p>
          </div>
        )}
        {mediaProcessing && (
          <div className="absolute bottom-1 left-2 text-xs text-grey-500 pointer-events-none">
            Processing file...
          </div>
        )}
      </div>
    );
  },
  (prevProps, nextProps) => {
    // Only re-render if relevant props change (not when selectedLead.messages changes)
    return (
      prevProps.selectedLead?.id === nextProps.selectedLead?.id &&
      prevProps.messageText === nextProps.messageText &&
      prevProps.textDisabled === nextProps.textDisabled &&
      prevProps.messageLoader === nextProps.messageLoader &&
      prevProps.loading === nextProps.loading &&
      prevProps.spinner === nextProps.spinner &&
      prevProps.leadsLoading === nextProps.leadsLoading &&
      prevProps.loaderFn === nextProps.loaderFn &&
      prevProps.originalText === nextProps.originalText &&
      prevProps.setAttachments === nextProps.setAttachments
    );
  }
);
